import {
    GpsCalibrationPoints,
    GpsPoint,
    HalfEvent,
    MatchPhone,
    PlottingData,
    Point,
    Sprint,
} from "refsix-js-models";
import { FC, useEffect, useRef, useState } from "react";
import pitchImage from "../../assets/images/pitch.png";
import "./pitchMap.css";
import "./sprintMap.css";
import { useDimensions } from "../hooks";
import { useInView } from "../hooks/useInView";
import StatsPeriodToggle from "./statsPeriodToggle";
import { COLORS_SPRINT_CATEGORY } from "../../constants/StatsColorPalette";
import { useTranslation } from "react-i18next";
import { IonCol, IonRow, IonText } from "@ionic/react";
import mapEventNameToDisplayName from "../../utils/MapEventToDisplayName";
import { hasFeatureAccess } from "../../services/subscriptionService";
import { copyObject, sprintsPositions } from "refsix-core";

interface SprintMapInterface {
    playingSegments: HalfEvent[];
    geoPoints: GpsPoint[];
    calibrationPoints: GpsCalibrationPoints;
    periodsNo: string;
    sprints: Sprint<number>[];
    match: MatchPhone;
    testId?: string;
}

const ARROW_HEAD_LENGTH = 10;
const ARROW_TAIL_WIDTH = 3;
const DEFAULT_WIDTH = 300;
const ANIMATION_DURATION_MS = 1500;
// Minimum time (as fraction of total animation) for a sprint to write in
const MIN_SPRINT_WRITE_FRACTION = 0.15; // ~225ms

interface AnimatedSprint {
    points: Point[];
    pointTimes: number[]; // normalized time (0-1) for each point in the path
    color: string;
}

const SprintMapComponent: FC<SprintMapInterface> = ({
    playingSegments,
    geoPoints,
    calibrationPoints,
    periodsNo,
    sprints,
    match,
    testId,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [containerDiv, dims] = useDimensions();
    const [selectedSegment, setSelectedSegment] = useState<number>(0);
    const [data, setData] = useState<PlottingData<Sprint<Point>[]> | null>(
        null
    );
    const sprintMapAccess = hasFeatureAccess("sprints");
    const { t } = useTranslation();
    const [filtersByCategory] = useState<boolean[]>([true, true, true]);
    const sprintCategoryColors = COLORS_SPRINT_CATEGORY;
    const sprintCategories = [
        t("fixture.FixtureSummaryStats.sprintmapKey.low"),
        t("fixture.FixtureSummaryStats.sprintmapKey.medium"),
        t("fixture.FixtureSummaryStats.sprintmapKey.high"),
    ];
    const [segmentsPlottedData, setSegmentsPlottedData] = useState<
        PlottingData<Sprint<Point>[]>[]
    >([]);
    const animationRef = useRef<number>(0);
    const inView = useInView(canvasRef, { threshold: 1.0 });

    useEffect(() => {
        const segmentsPlottedData = sprintsPositions(
            geoPoints,
            calibrationPoints,
            playingSegments,
            sprints,
            dims?.width || DEFAULT_WIDTH,
            null
        );
        setSegmentsPlottedData(segmentsPlottedData);

        // pick which playing segment to show e.g. [all, h1, h2, et1, et2...]
        const processedData = segmentsPlottedData[selectedSegment];
        if (processedData) {
            setData(processedData);
        }
    }, [
        dims,
        selectedSegment,
        geoPoints,
        calibrationPoints,
        playingSegments,
        sprints,
    ]);

    // Animate sprints writing in with proportional timing
    useEffect(() => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = 0;
        }

        const canvas = canvasRef.current;
        if (!canvas || !data || !inView) return;

        canvas.width = data.width;
        canvas.height = data.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const plottedSprints = data.data.filter(
            (s) => filtersByCategory[s.level - 1]
        );
        if (plottedSprints.length === 0) return;

        // Match original sprints to plotted sprints for point index timing
        const originalForSegment =
            selectedSegment === 0
                ? sprints
                : sprints.filter(
                      (s) => s.segment === selectedSegment - 1
                  );

        // Find global min/max point indices across all sprints for normalization
        let globalMin = Infinity;
        let globalMax = -Infinity;
        originalForSegment.forEach((s) => {
            s.points.forEach((idx) => {
                if (idx < globalMin) globalMin = idx;
                if (idx > globalMax) globalMax = idx;
            });
        });
        const globalRange = globalMax - globalMin || 1;

        // Build animated sprint data with per-point timing
        // Each sprint keeps its real start time but gets stretched to a
        // minimum write-in duration so the line visibly draws in
        const animatedSprints: AnimatedSprint[] = plottedSprints.map(
            (sprint, i) => {
                const originalPoints =
                    i < originalForSegment.length
                        ? originalForSegment[i].points
                        : [];

                // Get raw normalized times for each point
                const rawTimes = sprint.points.map((_, j) => {
                    const idx =
                        j < originalPoints.length ? originalPoints[j] : 0;
                    return (idx - globalMin) / globalRange;
                });

                // Stretch: keep the start time, but ensure the write-in
                // spans at least MIN_SPRINT_WRITE_FRACTION of the animation
                const sprintStart = rawTimes[0];
                const sprintEnd = rawTimes[rawTimes.length - 1];
                const rawDuration = sprintEnd - sprintStart;
                const stretchedDuration = Math.max(
                    rawDuration,
                    MIN_SPRINT_WRITE_FRACTION
                );

                const pointTimes = rawTimes.map((t) => {
                    if (rawDuration === 0) {
                        // All points have the same index — spread evenly
                        const idx = rawTimes.indexOf(t);
                        return (
                            sprintStart +
                            (idx / (rawTimes.length - 1 || 1)) *
                                stretchedDuration
                        );
                    }
                    // Remap from [sprintStart, sprintEnd] to [sprintStart, sprintStart + stretchedDuration]
                    const fraction =
                        (t - sprintStart) / rawDuration;
                    return sprintStart + fraction * stretchedDuration;
                });

                return {
                    points: sprint.points,
                    pointTimes,
                    color: COLORS_SPRINT_CATEGORY[sprint.level - 1],
                };
            }
        );

        // Find the latest point time (may exceed 1.0 due to stretching)
        let maxPointTime = 0;
        animatedSprints.forEach((s) =>
            s.pointTimes.forEach((t) => {
                if (t > maxPointTime) maxPointTime = t;
            })
        );
        const totalDurationMs = ANIMATION_DURATION_MS * Math.max(maxPointTime, 1);

        // Animation loop - clear and redraw each frame for smooth line interpolation
        const startTime = performance.now();
        const width = data.width;
        const height = data.height;

        function animate() {
            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / totalDurationMs, maxPointTime);

            ctx!.clearRect(0, 0, width, height);
            ctx!.lineWidth = ARROW_TAIL_WIDTH;

            for (const sprint of animatedSprints) {
                // Skip if sprint hasn't started yet
                if (sprint.pointTimes[0] > progress) continue;

                const points = sprint.points;
                ctx!.strokeStyle = sprint.color;
                ctx!.beginPath();
                ctx!.moveTo(points[0].x, points[0].y);

                let lastX = points[0].x;
                let lastY = points[0].y;
                let prevX = points[0].x;
                let prevY = points[0].y;
                let fullyDrawn = true;

                for (let j = 1; j < points.length; j++) {
                    if (sprint.pointTimes[j] <= progress) {
                        // This point is fully visible
                        ctx!.lineTo(points[j].x, points[j].y);
                        ctx!.moveTo(points[j].x, points[j].y);
                        prevX = lastX;
                        prevY = lastY;
                        lastX = points[j].x;
                        lastY = points[j].y;
                    } else {
                        // Interpolate to the partial position
                        fullyDrawn = false;
                        const prevTime = sprint.pointTimes[j - 1];
                        const nextTime = sprint.pointTimes[j];
                        const t =
                            (progress - prevTime) / (nextTime - prevTime);
                        const interpX =
                            points[j - 1].x +
                            t * (points[j].x - points[j - 1].x);
                        const interpY =
                            points[j - 1].y +
                            t * (points[j].y - points[j - 1].y);
                        ctx!.lineTo(interpX, interpY);
                        prevX = lastX;
                        prevY = lastY;
                        lastX = interpX;
                        lastY = interpY;
                        break;
                    }
                }

                // Draw arrowhead at current tip
                if (prevX !== lastX || prevY !== lastY) {
                    const dx = lastX - prevX;
                    const dy = lastY - prevY;
                    const angle = Math.atan2(dy, dx);
                    ctx!.moveTo(lastX, lastY);
                    ctx!.lineTo(
                        lastX -
                            ARROW_HEAD_LENGTH * Math.cos(angle - Math.PI / 6),
                        lastY -
                            ARROW_HEAD_LENGTH * Math.sin(angle - Math.PI / 6)
                    );
                    ctx!.moveTo(lastX, lastY);
                    ctx!.lineTo(
                        lastX -
                            ARROW_HEAD_LENGTH * Math.cos(angle + Math.PI / 6),
                        lastY -
                            ARROW_HEAD_LENGTH * Math.sin(angle + Math.PI / 6)
                    );
                }

                ctx!.stroke();
            }

            if (progress < maxPointTime) {
                animationRef.current = requestAnimationFrame(animate);
            }
        }

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = 0;
            }
        };
    }, [data, filtersByCategory, selectedSegment, sprints, inView]);

    // Filtering either the category in or out based on selection.
    function filterSprintsByCategory(
        sprintsForSegment: PlottingData<Sprint<Point>[]>
    ) {
        // Copying sprintsForSegment into sprints.
        const sprints = copyObject(sprintsForSegment);
        sprints.data = sprintsForSegment.data.filter(function (sprint) {
            return filtersByCategory[sprint.level - 1];
        });
        return sprints;
    }

    // Updating sprintMap based on which sprintCategories is chosen.
    function updateSprintMap() {
        if (segmentsPlottedData) {
            const sprintsForSegment = segmentsPlottedData[selectedSegment];
            const filteredSprintsByCategory =
                filterSprintsByCategory(sprintsForSegment);
            if (filteredSprintsByCategory) {
                // Setting the new filtered data and drawing sprintMap again
                setData(filteredSprintsByCategory);
            }
        }
    }

    function showHideCategories(index: number) {
        filtersByCategory[index] = !filtersByCategory[index];
        updateSprintMap();
    }

    function hasMoreThanOneSegment() {
        return playingSegments && playingSegments.length > 1;
    }

    // Adding the sprints to get the total number for each period
    function totalSprintsForPeriods(sprints: number[]) {
        return sprints.reduce((a, b) => a + b, 0);
    }

    // returning number of sprints for periods
    function halvesDistance(index: number): number | null {
        const stats = match.stats;
        if (!stats) return null;
        let sprints: number[];

        switch (match.periodsNo) {
            case "1":
                sprints = stats.sprintsTotal;
                break;
            case "3":
                sprints = stats.sprintsByThirdsTotal[index];
                break;
            case "4":
                sprints = stats.sprintsByQuartersTotal[index];
                break;
            case "2":
            default:
                sprints = stats.sprintsByHalvesTotal[index];
        }

        return totalSprintsForPeriods(sprints);
    }

    return (
        <div ref={containerDiv} data-testid={testId}>
            {sprintMapAccess && (
                <>
                    <IonCol>
                        {/* Calculating total sprints*/}
                        <IonRow>
                            <IonCol size="6">
                                <IonText className="white-text-and-font">
                                    {t("stats.stats.Sprints.totalSprints")}
                                </IonText>
                            </IonCol>
                            <IonCol size="6" className="align-right">
                                {/* Calculating total sprints for all categories*/}
                                <IonText className="icon-date">
                                    {match.stats?.sprintsTotal.reduce(
                                        (total, currentValue) =>
                                            total + currentValue,
                                        0
                                    )}
                                </IonText>
                            </IonCol>
                        </IonRow>

                        {/* Calculating total for categories, low, medium and high*/}
                        {match.stats?.sprintsTotal.map((value, index) => {
                            return (
                                <IonRow key={index}>
                                    <IonCol size="6">
                                        <IonText className="matches-with-text match-details-desc">
                                            {sprintCategories[index]}
                                        </IonText>
                                    </IonCol>
                                    <IonCol size="6" className="align-right">
                                        <IonText className="icon-date">
                                            {value}
                                        </IonText>
                                    </IonCol>
                                </IonRow>
                            );
                        })}
                    </IonCol>

                    {/* Calculating the different sprints for the different periods*/}
                    {playingSegments.length > 1 &&
                        playingSegments &&
                        playingSegments.map((seg, index) => {
                            return (
                                <IonRow
                                    key={index}
                                    hidden={!hasMoreThanOneSegment}
                                >
                                    <IonCol size="6">
                                        <IonText className="white-text-and-font">
                                            {mapEventNameToDisplayName(seg, t)}
                                        </IonText>
                                    </IonCol>
                                    <IonCol size="6" className="align-right">
                                        <IonText className="icon-date">
                                            {halvesDistance(index)?.toFixed(0)}
                                        </IonText>
                                    </IonCol>
                                </IonRow>
                            );
                        })}
                </>
            )}

            <StatsPeriodToggle
                playingSegments={playingSegments}
                periodsNo={periodsNo}
                onSegmentChange={setSelectedSegment}
            />
            <div className="map-container" data-testid="mapContainer">
                <div className="responsive svg-pitch">
                    <img src={pitchImage} alt="Pitch image" />
                </div>
                <canvas ref={canvasRef} className="canvas" />
                <IonCol className="sprint-key-container">
                    {sprintCategories.map((key, index) => {
                        return (
                            <span
                                className={`sprint-key ${
                                    filtersByCategory[index] ? "" : "hidden"
                                }`}
                                key={index}
                                data-testid={"sprintCategory" + index}
                                onClick={() => showHideCategories(index)}
                            >
                                <span
                                    style={{
                                        backgroundColor:
                                            sprintCategoryColors[index],
                                    }}
                                    className="ion-stop"
                                />
                                {key}
                            </span>
                        );
                    })}
                </IonCol>
            </div>
        </div>
    );
};
export default SprintMapComponent;
